import { useState } from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { state } from '../store/global';
import API from '../lib/api';
import findMessage from '../utils/messages'; // Helper function defined below

import LoginLeftSide from '../components/common/LoginLeftSide';
import Selectbox from '../components/common/Selectbox';
import Inputbox from '../components/common/Inputbox';
import NormalButton from '../components/common/NormalButton';
import LoginFooter from '../components/common/LoginFooter';

import LogoImage from '../assets/images/Logo_blue_2x.svg';

export default function Login() {
  const navigate = useNavigate();

  const [userType, setUserType] = useState('Personas'); // Personas, Empresas
  const [errorRole, setErrorRole] = useState(false);
  const [email, setEmail] = useState('');
  const [errorEmail, setErrorEmail] = useState(false);
  const [password, setPassword] = useState('');
  const [errorPassword, setErrorPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const onLogin = async () => {
    if (!email || !password) return;

    // Call API here
    await API.post('auth/login', {
      role: userType == 'Personas' ? 'individual' : 'business',
      email: email,
      password: password,
    })
      .then((res) => {
        localStorage.setItem('token', res.data.token);
        state.setUser(res.data.user);
        navigate('/login/phoneSet');
      })
      .catch((err) => {
        const msg = err.response?.data?.message || err.message;
        if (msg == 'Wrong user role') {
          setErrorRole(true);
          setErrorEmail(false);
          setErrorPassword(false);
          setErrorMessage('');
        } else if (msg == 'User not found') {
          setErrorRole(false);
          setErrorEmail(true);
          setErrorPassword(false);
          setErrorMessage('');
        } else if (msg == 'Wrong password') {
          setErrorRole(false);
          setErrorEmail(false);
          setErrorPassword(true);
          setErrorMessage('');
        } else if (msg == 'You have attempted to log in more than 5 times') {
          setErrorRole(false);
          setErrorEmail(false);
          setErrorPassword(false);
          setErrorMessage(findMessage(msg));
        } else {
          setErrorRole(false);
          setErrorEmail(false);
          setErrorPassword(false);
          setErrorMessage(findMessage(msg));
        }
        console.log('err: ', msg);
      });
  };

  const allInputOk = () => {
    return email && password;
  };

  return (
    <div className="relative flex min-h-screen">
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        {/* Modal content with exact size */}
        <div className="relative z-10 bg-white rounded-2xl shadow-xl w-[1044px] h-[700px] overflow-hidden">
          <div className="flex md:flex-row w-full h-full">
            {/* Left Side - Image */}
            <LoginLeftSide />

            {/* Right Side - Form */}
            <div className="w-full h-full flex flex-col justify-between md:w-1/2 p-10 bg-[#F9F9F9]">
              <div className="w-full h-[32px] relative">
                <button
                  onClick={() => navigate('/')}
                  className="absolute right-0 top-0 text-[32px] w-[32px] h-[32px] flex items-center justify-center text-gray-500 hover:text-gray-700 text-sm"
                >
                  <X className="w-5 h-5 text-[#0A0D12] cursor-pointer" />
                </button>
              </div>

              <div className="h-full">
                <div className="font-[poppins] font-semibold font-[400] md:px-14 space-y-8">
                  <div className="mb-4 flex items-center justify-center">
                    <img src={LogoImage} alt="App Logo" />
                  </div>
                  <h2 className="text-xl text-[#092F7B] font-bold leading-[20px] tracking-[0%]">
                    ¡Bienvenido de vuelta!
                  </h2>

                  <div className="space-y-1">
                    <Selectbox
                      options={['Personas', 'Empresas']}
                      selected={userType}
                      onChange={setUserType}
                    />
                    <p className="font-[poppins] text-xs text-[#F04438] font-normal">
                      {errorRole ? 'El rol de usuario no es correcto' : ''}
                    </p>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-1">
                      <Inputbox
                        inputClass="bg-white"
                        title="Correo electrónico"
                        titleClass="text-[#717680]"
                        placeholder="ejemplo@mail.com"
                        value={email}
                        onChange={setEmail}
                      />
                      <p className="font-[poppins] text-xs text-[#F04438] font-normal">
                        {errorEmail ? 'Usuario no encontrado' : ''}
                      </p>
                    </div>

                    <div className="space-y-1">
                      <div className="h-[20px] text-xs text-[#717680] font-normal leading-[20px] tracking-[0%]">
                        Contraseña
                      </div>
                      <div className="space-y-1">
                        <div className="relative">
                          <input
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Contraseña123#"
                            className="w-full text-sm placeholder-[#A4A7AE] text-[#414651] font-normal focus:outline-none focus:border-[#414651] bg-white border border-[#C2C2C2] py-2 pl-3 pr-10 rounded-[10px]"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((prev) => !prev)}
                            className="absolute right-2.5 top-2.5 text-gray-500"
                          >
                            {showPassword ? (
                              <EyeOff size={16} />
                            ) : (
                              <Eye size={16} />
                            )}
                          </button>
                        </div>
                        <p className="font-[poppins] text-xs text-[#F04438] font-normal">
                          {errorPassword ? 'Usuario no encontrado' : ''}
                        </p>
                      </div>
                      <button
                        onClick={() => navigate('/forgotPassword')}
                        className="text-xs text-[#252B37] font-medium hover:underline hover:text-[#092F7B] cursor-pointer leading-[20px] tracking-[0%]"
                      >
                        ¿Olvidaste tu contraseña?
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1">
                    <NormalButton
                      customClass={`w-full ${
                        allInputOk()
                          ? 'bg-[#092F7B] text-white'
                          : 'bg-[#E9EAEB] text-[#A4A7AE]'
                      }`}
                      text="Ingresar"
                      onClick={onLogin}
                    />

                    <div className="text-xs text-[#181D27] font-normal leading-[20px] tracking-[0%] text-center">
                      ¿No tienes cuenta?{' '}
                      <button
                        onClick={() => navigate('/register')}
                        className="font-bold cursor-pointer hover:underline hover:text-[#092F7B]"
                      >
                        Regístrate
                      </button>
                    </div>
                  </div>
                </div>

                <p className="pt-10 md:px-14 font-[poppins] text-center text-sm text-[#F04438] font-normal">
                  {errorMessage}
                </p>
              </div>

              {/* Login footer */}
              <LoginFooter onClick={() => navigate('/contact')} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
